import React, { useMemo, useState } from 'react';
import { Input, Button, Checkbox, Tooltip } from 'antd';
import {
  SearchOutlined, ClearOutlined, ThunderboltOutlined,
  DashboardOutlined, TeamOutlined, FileProtectOutlined, DollarOutlined,
  ExclamationCircleOutlined, WarningOutlined, BarChartOutlined, BankOutlined,
  SafetyOutlined, EnvironmentOutlined, SettingOutlined, UserSwitchOutlined,
  AuditOutlined, MessageOutlined, AppstoreOutlined, SafetyCertificateOutlined,
} from '@ant-design/icons';
import { PERMISSION_GROUPS, PERMISSION_MODULES, ROLE_PRESETS } from '../common/auth/permissions';
import './permissionsSelector.scss';

const MODULE_ICONS = {
  'Tableau de bord': <DashboardOutlined />,
  'Gestion': <AppstoreOutlined />,
  'Rapports': <BarChartOutlined />,
  'Configuration': <SettingOutlined />,
  'Administration': <UserSwitchOutlined />,
};

const GROUP_ICONS = {
  'Tableau de bord': <DashboardOutlined />,
  'Clients': <TeamOutlined />,
  'Contrats': <FileProtectOutlined />,
  'Règlements': <DollarOutlined />,
  'Impayés': <ExclamationCircleOutlined />,
  'Sinistres': <WarningOutlined />,
  'Bilans & États': <BarChartOutlined />,
  'Compagnies': <BankOutlined />,
  "Types d'assurance": <SafetyOutlined />,
  'Agences': <EnvironmentOutlined />,
  'Gestion des accès': <UserSwitchOutlined />,
  'Rôles': <SafetyCertificateOutlined />,
  'Audit': <AuditOutlined />,
  'SMS': <MessageOutlined />,
};

const PRESETS = [
  { key: 'responsableAgence', label: 'Resp. agence' },
  { key: 'commercial', label: 'Commercial' },
  { key: 'comptable', label: 'Comptable' },
  { key: 'consultation', label: 'Consultation' },
  { key: 'superAdmin', label: 'Accès total' },
];

export const ALL_PERMS = PERMISSION_GROUPS.flatMap((g) => g.items.map((i) => i.value));

const PermissionsSelector = ({ value = [], onChange, showPresets = true }) => {
  const [search, setSearch] = useState('');
  const selectedPerms = value;

  const set = (next) => onChange?.(next);

  const togglePerm = (perm) => {
    set(selectedPerms.includes(perm)
      ? selectedPerms.filter((p) => p !== perm)
      : [...selectedPerms, perm]);
  };

  const toggleGroup = (perms) => {
    const allSelected = perms.every((p) => selectedPerms.includes(p));
    set(allSelected
      ? selectedPerms.filter((p) => !perms.includes(p))
      : [...new Set([...selectedPerms, ...perms])]);
  };

  const applyPreset = (key) => {
    const fn = ROLE_PRESETS[key];
    if (fn) set(fn());
  };

  const selectedCount = useMemo(
    () => selectedPerms.filter((p) => ALL_PERMS.includes(p)).length,
    [selectedPerms],
  );

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return PERMISSION_GROUPS;
    return PERMISSION_GROUPS
      .map((g) => {
        const groupMatch = g.title.toLowerCase().includes(q) || (g.module || '').toLowerCase().includes(q);
        const items = groupMatch ? g.items : g.items.filter((i) => i.label.toLowerCase().includes(q));
        return { ...g, items };
      })
      .filter((g) => g.items.length > 0);
  }, [search]);

  const moduleSections = useMemo(() => {
    return PERMISSION_MODULES
      .map((mod) => {
        const groups = filteredGroups.filter((g) => g.module === mod);
        const perms = groups.flatMap((g) => g.items.map((i) => i.value));
        const sel = perms.filter((p) => selectedPerms.includes(p)).length;
        return { module: mod, groups, total: perms.length, selected: sel };
      })
      .filter((m) => m.groups.length > 0);
  }, [filteredGroups, selectedPerms]);

  return (
    <>
      {/* Toolbar */}
      <div className="role-perms-toolbar">
        <Input
          allowClear
          className="role-perms-search"
          placeholder="Rechercher une permission…"
          prefix={<SearchOutlined style={{ color: 'var(--cap-text-muted)' }} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {showPresets && (
          <div className="role-perms-presets">
            <span className="role-perms-preset-label">Profils</span>
            {PRESETS.map((p) => (
              <Tooltip key={p.key} title={`Appliquer le profil « ${p.label} »`}>
                <span className="role-preset-chip" onClick={() => applyPreset(p.key)}>
                  <ThunderboltOutlined /> {p.label}
                </span>
              </Tooltip>
            ))}
          </div>
        )}

        <div className="role-perms-counter">
          <AppstoreOutlined />
          <span><strong>{selectedCount}</strong> / {ALL_PERMS.length} permission(s)</span>
          {selectedCount > 0 && (
            <Button
              size="small"
              icon={<ClearOutlined />}
              className="role-perms-clear-btn"
              onClick={() => set([])}
            >
              Tout décocher
            </Button>
          )}
        </div>
      </div>

      {/* Liste par module */}
      <div className="role-perms-list">
        {moduleSections.length === 0 ? (
          <div className="role-perms-empty">Aucune permission ne correspond à « {search} ».</div>
        ) : moduleSections.map((section) => {
          const modPerms = section.groups.flatMap((g) => g.items.map((i) => i.value));
          const modAll = modPerms.length > 0 && section.selected === modPerms.length;
          const modSome = section.selected > 0 && !modAll;
          return (
            <section key={section.module} className="role-perm-module">
              <div className="role-perm-module-head">
                <Checkbox
                  checked={modAll}
                  indeterminate={modSome}
                  onChange={() => toggleGroup(modPerms)}
                />
                <span className="role-perm-module-icon">
                  {MODULE_ICONS[section.module] || <AppstoreOutlined />}
                </span>
                <span className="role-perm-module-title" onClick={() => toggleGroup(modPerms)}>
                  {section.module}
                </span>
                <span className={`role-perm-module-count ${modAll ? 'is-full' : modSome ? 'is-partial' : ''}`}>
                  {section.selected}/{section.total}
                </span>
              </div>

              <div className="role-perm-module-groups">
                {section.groups.map((group) => {
                  const groupPerms = group.items.map((i) => i.value);
                  const selInGroup = groupPerms.filter((p) => selectedPerms.includes(p)).length;
                  const allSel = groupPerms.length > 0 && selInGroup === groupPerms.length;
                  const someSel = selInGroup > 0 && !allSel;
                  return (
                    <div key={group.title} className={`role-perm-group ${selInGroup > 0 ? 'is-active' : ''}`}>
                      <div className="role-perm-group-head">
                        <Checkbox
                          checked={allSel}
                          indeterminate={someSel}
                          onChange={() => toggleGroup(groupPerms)}
                        />
                        <span className="role-perm-group-icon">
                          {GROUP_ICONS[group.title] || <AppstoreOutlined />}
                        </span>
                        <span
                          style={{ fontWeight: 700, cursor: 'pointer', color: 'var(--cap-text)' }}
                          onClick={() => toggleGroup(groupPerms)}
                        >
                          {group.title}
                        </span>
                        <span className={`role-perm-group-count ${allSel ? 'is-full' : someSel ? 'is-partial' : ''}`}>
                          {selInGroup}/{groupPerms.length}
                        </span>
                      </div>
                      <div className="role-perm-group-body">
                        {group.items.map((item) => {
                          const checked = selectedPerms.includes(item.value);
                          return (
                            <Checkbox
                              key={item.value}
                              className={`role-perm-item ${checked ? 'is-checked' : ''}`}
                              checked={checked}
                              onChange={() => togglePerm(item.value)}
                            >
                              {item.label}
                            </Checkbox>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </>
  );
};

export default PermissionsSelector;
